import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionReady, setSessionReady] = useState(false);
  const [contextReady, setContextReady] = useState(false);
  const [contextError, setContextError] = useState(null);
  // Prevent duplicate profile fetches
  const profileFetchingRef = useRef(false);
  const lastFetchedUserIdRef = useRef(null);

  const fetchUserProfile = useCallback(async (userId, { background = false } = {}) => {
    // Deduplicate: skip if already fetching for this user
    if (profileFetchingRef?.current && lastFetchedUserIdRef?.current === userId) return;
    profileFetchingRef.current = true;
    lastFetchedUserIdRef.current = userId;

    try {
      setContextError(null);

      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) {
        console.error('[AuthContext] Error fetching user profile:', error?.message);
        setUserProfile(null);
        setContextReady(false);
        setContextError('No se pudo cargar el perfil de usuario.');
        return;
      }

      setUserProfile(data);

      if (data?.company_id) {
        setContextReady(true);
        setContextError(null);
      } else {
        setContextReady(false);
        setContextError('Perfil incompleto: falta company_id. Contacta al administrador.');
      }
    } catch (err) {
      console.error('[AuthContext] Failed to fetch user profile:', err);
      setUserProfile(null);
      setContextReady(false);
      setContextError('Error al cargar perfil de usuario.');
    } finally {
      profileFetchingRef.current = false;
      // Only update loading from profile fetch if it was a foreground call
      if (!background) {
        setLoading(false);
      }
    }
  }, []);

  const refreshContext = useCallback(async () => {
    if (user) {
      lastFetchedUserIdRef.current = null; // force re-fetch
      await fetchUserProfile(user?.id);
    }
  }, [user, fetchUserProfile]);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const { data: { session } } = await supabase?.auth?.getSession();

        if (!mounted) return;

        if (session?.user) {
          setUser(session?.user);
          setSessionReady(true);
          // Unblock UI immediately — profile loads in background
          setLoading(false);
          fetchUserProfile(session?.user?.id, { background: true });
        } else {
          setUser(null);
          setUserProfile(null);
          setSessionReady(true);
          setContextReady(false);
          setLoading(false);
        }
      } catch (err) {
        console.error('[AuthContext] Session init error:', err);
        if (mounted) {
          setUser(null);
          setSessionReady(true);
          setContextReady(false);
          setLoading(false);
        }
      }
    };

    init();

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      // Skip INITIAL_SESSION — handled by init()
      if (event === 'INITIAL_SESSION') return;

      if (session?.user) {
        setUser(session?.user);
        setSessionReady(true);
        setLoading(false);
        // Only fetch profile if it's a new user or explicit sign-in
        if (lastFetchedUserIdRef?.current !== session?.user?.id || event === 'SIGNED_IN') {
          lastFetchedUserIdRef.current = null; // allow re-fetch on sign-in
          fetchUserProfile(session?.user?.id, { background: true });
        }
      } else {
        setUser(null);
        setUserProfile(null);
        setSessionReady(true);
        setContextReady(false);
        setLoading(false);
        profileFetchingRef.current = false;
        lastFetchedUserIdRef.current = null;
      }
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [fetchUserProfile]);

  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: { data: metadata }
      });
      if (error) throw new Error(error.message);
      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      if (!email || !password) {
        return { data: null, error: new Error('Correo y contraseña son requeridos') };
      }
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email: email?.trim(),
        password
      });
      if (error) throw new Error(error.message);
      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      setUser(null);
      setUserProfile(null);
      setSessionReady(false);
      setContextReady(false);
      profileFetchingRef.current = false;
      lastFetchedUserIdRef.current = null;
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/reset-password`
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Reset password error:', error);
      return { data: null, error };
    }
  };

  const updateUserMetadata = async (metadata) => {
    try {
      const { data, error } = await supabase?.auth?.updateUser({ data: metadata });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update metadata error:', error);
      return { data: null, error };
    }
  };

  const isSchemaError = (error) => {
    if (!error) return false;
    if (error?.code && typeof error?.code === 'string') {
      const errorClass = error?.code?.substring(0, 2);
      if (errorClass === '42' || errorClass === '08') return true;
      if (errorClass === '23') return false;
    }
    if (error?.message) {
      const schemaErrorPatterns = [
        /relation.*does not exist/i,
        /column.*does not exist/i,
        /function.*does not exist/i,
        /syntax error/i,
        /invalid.*syntax/i,
        /type.*does not exist/i,
        /undefined.*column/i,
        /undefined.*table/i,
        /undefined.*function/i,
      ];
      return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
    }
    return false;
  };

  const value = {
    user,
    userProfile,
    loading,
    sessionReady,
    contextReady,
    contextError,
    refreshContext,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateUserMetadata,
    isSchemaError
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
